import {
	formatRubles,
	MAX_INVOICE_DESCRIPTION_LENGTH,
	MAX_INVOICE_TITLE_LENGTH,
	type ReceiptConfig,
} from "@velizhanin-dev/bot-scenario"
import { Field } from "@/shared/ui/form-field"
import { Input } from "@/shared/ui/input"
import { NativeSelect } from "@/shared/ui/native-select"
import { TextWithVariables } from "@/shared/ui/text-with-variables"
import { VariableSelect } from "@/shared/ui/variable-select"
import { Checkbox } from "./checkbox-label"
import {
	type EditorProps,
	parseOptionalNumber,
	readNumber,
	readString,
	withField,
} from "./editor-props"
import { ContactField, ReceiptFields } from "./invoice-receipt-fields"
import { issuesAt } from "./issues-text"
import { DEFAULT_RECEIPT } from "./lib/invoice-labels"
import { TimeoutField } from "./timeout-field"

/** Счёт Telegram Payments через ЮKassa: цена, что спросить у покупателя, чек по 54-ФЗ. */
export function InvoiceEditor({ config, onChange, issues, draft }: EditorProps) {
	const priceFromVariable = typeof config.amountVariable === "string"
	const amount = readNumber(config, "amount")
	const receipt = config.receipt as ReceiptConfig | undefined
	const setFlag = (key: string, value: boolean) =>
		onChange(withField(config, key, value || undefined))

	return (
		<div className="space-y-4">
			<p className="rounded-lg border border-border/60 bg-muted/40 p-2 text-xs text-muted-foreground">
				Оплата проходит внутри Telegram через ЮKassa. Токен провайдера подключается на странице
				бота. В тест-чате оплату можно имитировать, деньги не списываются.
			</p>

			<Field
				label="Название"
				issues={issuesAt(issues, "title")}
				hint={`До ${MAX_INVOICE_TITLE_LENGTH} символов. Так счёт увидит покупатель`}
			>
				<TextWithVariables
					multiline={false}
					maxLength={MAX_INVOICE_TITLE_LENGTH}
					placeholder="Разбор канала"
					value={readString(config, "title")}
					onChange={(title) => onChange({ ...config, title })}
					variables={draft.variables}
				/>
			</Field>
			<Field
				label="Описание"
				issues={issuesAt(issues, "description")}
				hint={`До ${MAX_INVOICE_DESCRIPTION_LENGTH} символов`}
			>
				<TextWithVariables
					rows={3}
					maxLength={MAX_INVOICE_DESCRIPTION_LENGTH}
					placeholder="Что получит покупатель после оплаты"
					value={readString(config, "description")}
					onChange={(description) => onChange({ ...config, description })}
					variables={draft.variables}
				/>
			</Field>

			<Field
				label="Цена"
				issues={[...issuesAt(issues, "amount"), ...issuesAt(issues, "amountVariable")]}
			>
				<NativeSelect
					aria-label="Откуда цена"
					value={priceFromVariable ? "variable" : "fixed"}
					onChange={(event) =>
						onChange(
							event.target.value === "variable"
								? withField({ ...config, amountVariable: "" }, "amount", undefined)
								: withField({ ...config, amount: amount ?? 1000 }, "amountVariable", undefined),
						)
					}
				>
					<option value="fixed">Фиксированная</option>
					<option value="variable">Из числовой переменной</option>
				</NativeSelect>
				{priceFromVariable ? (
					<VariableSelect
						aria-label="Переменная с ценой"
						value={readString(config, "amountVariable")}
						onChange={(amountVariable) => onChange({ ...config, amountVariable })}
						variables={draft.variables.filter((variable) => variable.type === "NUMBER")}
					/>
				) : (
					<div className="flex items-center gap-2">
						<Input
							type="number"
							min={1}
							step="0.01"
							aria-label="Цена в рублях"
							placeholder="990"
							value={amount ?? ""}
							onChange={(event) =>
								onChange(withField(config, "amount", parseOptionalNumber(event.target.value)))
							}
						/>
						<span className="shrink-0 text-sm text-muted-foreground">
							{amount !== undefined ? formatRubles(amount) : "₽"}
						</span>
					</div>
				)}
			</Field>

			<Field
				label="Картинка"
				issues={issuesAt(issues, "photoUrl")}
				hint="Публичная ссылка https на изображение; можно без картинки"
			>
				<Input
					aria-label="Ссылка на картинку счёта"
					placeholder="https://…/cover.jpg"
					inputMode="url"
					value={readString(config, "photoUrl")}
					onChange={(event) =>
						onChange(withField(config, "photoUrl", event.target.value.trim() || undefined))
					}
				/>
			</Field>

			<Field label="Что спросить при оплате" hint="Telegram попросит заполнить это перед оплатой">
				<div className="space-y-2">
					<ContactField
						config={config}
						onChange={onChange}
						draft={draft}
						field="Name"
						label="Имя"
						requiredByReceipt={false}
					/>
					<ContactField
						config={config}
						onChange={onChange}
						draft={draft}
						field="Phone"
						label="Телефон"
						requiredByReceipt={receipt?.contact === "phone"}
					/>
					<ContactField
						config={config}
						onChange={onChange}
						draft={draft}
						field="Email"
						label="Email"
						requiredByReceipt={receipt?.contact === "email"}
					/>
				</div>
			</Field>

			<Field label="Чек по 54-ФЗ" hint="ЮKassa пробьёт чек и пришлёт его покупателю">
				<Checkbox
					label="Отправлять чек"
					checked={!!receipt}
					onChange={(checked) =>
						onChange(withField(config, "receipt", checked ? DEFAULT_RECEIPT : undefined))
					}
				/>
				<ReceiptFields config={config} onChange={onChange} issues={issues} receipt={receipt} />
			</Field>

			<TimeoutField config={config} onChange={onChange} what="оплаты" />
			<div className="space-y-1">
				<Checkbox
					label="Без звука уведомления"
					checked={config.silent === true}
					onChange={(checked) => setFlag("silent", checked)}
				/>
				<Checkbox
					label="Запретить пересылку счёта"
					checked={config.protect === true}
					onChange={(checked) => setFlag("protect", checked)}
				/>
			</div>
		</div>
	)
}
